import { CommonModule } from '@angular/common';
import { Component, computed, inject, signal } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

//dx / vendors
import { Item as DxMenuItem } from 'devextreme/ui/menu';

//app
import {
    AppPermissions,
    AuthenticationService,
    IUserAccountDto,
} from '@my-aon/auth';
import { CacheService } from '@my-aon/shared';
import { hasValue } from '@my-aon/utils';
import { Navigation, NavigationService } from '../../lib';

import { LogoComponent } from '../logo/logo.component';
import { LanguageSelectorComponent } from '../language-selector/language-selector.component';
import { VendorsModule } from '@my-aon/vendors';

type NavbarMenuItem = Omit<DxMenuItem, 'icon' | 'items'> & Omit<Navigation, 'items'> & {
    items?: NavbarMenuItem[];
    /** Children kept for active-state checks when dropdown is suppressed (inline mode). */
    nestedItems?: NavbarMenuItem[];
    parentPath?: string;
};

@Component({
    selector: 'app-navbar',
    templateUrl: './navbar.component.html',
    styleUrl: './navbar.component.scss',
    imports: [
        CommonModule,
        VendorsModule,
        LogoComponent,
        LanguageSelectorComponent,
    ],
    host: {
        class: 'shadow-sm',
    },
})
export class NavbarComponent {

    readonly router: Router = inject(Router);
    readonly auth: AuthenticationService = inject(AuthenticationService);
    readonly cacheService: CacheService = inject(CacheService);
    readonly navigationService: NavigationService = inject(NavigationService);


    $userInfo = computed(() => this.auth.$user());
    $userClientSplit = computed(() => this.$userInfo()?.Clients.length == 1);
    $userClientSecondary = computed(() => {
        const userInfo = this.$userInfo();
        if (!userInfo) return undefined;
        return userInfo.XpressClientGroupName?.toLowerCase();
    })
    $userClientPrimary = computed(() => {
        const userInfo = this.$userInfo();
        if (!userInfo) return undefined;
        return userInfo.Clients.length == 1 ? userInfo.Clients[0].Name?.toLowerCase() : userInfo.XpressClientGroupName?.toLowerCase();
    });

    $userSignature = computed(() => {
        const userInfo = this.$userInfo();
        if (!userInfo) return undefined;
        const userNameParts = userInfo.Name.split(' ');
        let signature = userNameParts[0][0];
        if (userNameParts.length > 1)
            signature += userNameParts[userNameParts.length - 1][0];
        return signature;
    });

    get canReadGeneralDocumentation() {
        return this.auth.$user()
            ?.PermissionList.includes(
                AppPermissions.GENERAL_DOCUMENTATION_READ_DOCUMENTATION_GENERAL,
            );
    }

    /** Rebuilds when nav config or UserInfo changes (permissions / module flags). */
    $navigation = computed(() => {
        this.$userInfo();
        return this.buildNavigation(this.navigationService.$navigation() ?? []);
    });

    selectedRoute?: string;

    constructor() {
        this.router.events
            .pipe(takeUntilDestroyed())
            .subscribe((val) => {
                if (val instanceof NavigationEnd) {
                    this.selectedRoute = val.urlAfterRedirects.split('?')[0];
                }
            });
    }

    setLanguage(lang: string) {
        this.cacheService.set('lang', lang);
    }

    logout() {
        this.auth.signOut();
    }

    routerLink(path?: string, rootPath?: string): string | undefined {
        if (!path) return undefined;
        if (hasValue(rootPath) && path.startsWith('/')) return path; //when child paths not in same parentRootPath
        if (path.startsWith('http')) return path; //admin external paths
        return '/' + [rootPath, path].filter((x) => hasValue(x)).join('/');
    }

    routerParams(item: Navigation) {
        return item.queryParams;
    }

    iconType(item: Navigation): 'txt' | 'fa' | 'bi' | 'mat' | null {
        if (hasValue(item?.icon?.mat)) return 'mat';
        if (hasValue(item?.icon?.fa)) return 'fa';
        if (hasValue(item?.icon?.bi)) return 'bi';
        if (hasValue(item?.icon?.txt)) return 'txt';
        return null;
    }

    hasChildren(item: NavbarMenuItem | Navigation): boolean {
        const nested = (item as NavbarMenuItem).nestedItems ?? item.items;
        return Array.isArray(nested) && nested.length > 0;
    }

    private buildNavigation(navigation: Navigation[]): NavbarMenuItem[] {
        const filtered = this.filterByPermission(navigation);

        // Inline (no dropdown) only when the user can access exactly one top-level module.
        // e.g. auto alone → flatten; auto+admin → keep both as module dropdowns/links.
        if (filtered.length === 1) {
            const mod = filtered[0];
            const children = mod.items ?? [];
            if (children.length > 0) {
                return children.map((child) =>
                    this.toMenuItem(child, mod.path, { suppressDropdown: true }),
                );
            }
            return [this.toMenuItem(mod, undefined, { suppressDropdown: true })];
        }

        return filtered.map((item) => this.toMenuItem(item));
    }

    private filterByPermission(items: Navigation[]): Navigation[] {
        const canAccess = (item: Navigation) =>
            this.auth.hasAllPermissions(...(item.requiredPermissions ?? [])) &&
            this.auth.hasAnyPermission(...(item.requiredPermissionsAny ?? [])) &&
            this.auth.hasModuleAccess(item.id as any);

        return items
            .filter(canAccess)
            .map((item) => {
                const children = item.items
                    ? this.filterByPermission(item.items)
                    : undefined;
                return {
                    ...item,
                    items: children,
                };
            })
            .filter((item) => {
                const hadChildren = Array.isArray(item.items);
                if (hadChildren && (item.items?.length ?? 0) === 0) {
                    // Parent with all children filtered out — hide unless it is still a useful leaf path
                    // (e.g. admin root is navigable even when we strip children for inline mode later)
                    return false;
                }
                return true;
            });
    }

    private toMenuItem(
        item: Navigation,
        parentPath?: string,
        options?: { suppressDropdown?: boolean },
    ): NavbarMenuItem {
        // Deeper levels never get a submenu — grandchildren are listed in app-sub-navbar.
        const nestedItems = item.items?.map((child) =>
            this.toMenuItem(
                child,
                [parentPath, item.path].filter(hasValue).join('/'),
                { suppressDropdown: true },
            ),
        );

        const showDropdown =
            !options?.suppressDropdown &&
            Array.isArray(nestedItems) &&
            nestedItems.length > 0;

        return {
            ...item,
            // dx-menu only gets items when we want a dropdown; nestedItems keep active-state info
            items: showDropdown ? nestedItems : undefined,
            nestedItems,
            parentPath,
            template: showDropdown ? 'parentTemplate' : 'linkTemplate',
        };
    }

    // Returns true if this item or any nested child matches the current route
    isModuleActive(item: NavbarMenuItem): boolean {
        const currentUrl = this.router.url.split('?')[0];
        const children = item.nestedItems ?? item.items ?? [];

        if (children.some((child) => this.isModuleActive(child))) {
            return true;
        }

        const fullPath = this.resolveFullPath(item);
        if (!fullPath) return false;
        return currentUrl === fullPath || currentUrl.startsWith(fullPath + '/');
    }

    private resolveFullPath(item: NavbarMenuItem): string | undefined {
        if (item.path?.startsWith('http')) return item.path;
        if (item.path?.startsWith('/')) return item.path;

        const segments = [item.parentPath, item.path].filter(
            (segment) => segment !== null && segment !== undefined && segment !== '',
        );
        if (!segments.length) return undefined;
        return '/' + segments.join('/');
    }
}
